import React, { useState, useEffect, useRef, useCallback } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import PhaseTimelineCard from './components/PhaseTimelineCard';
import DeliverableRow from './components/DeliverableRow';
import AddPhaseModal from './components/AddPhaseModal';
import AddDeliverableModal from './components/AddDeliverableModal';
import ResourceConflictAlert from './components/ResourceConflictAlert';
import ProjectSelector from './components/ProjectSelector';
import { supabase } from '../../lib/supabase';
import { projectService } from '../../services/projectService';
import { resourceService } from '../../services/resourceService';
import { createRealtimeSubscription, RealtimeErrorType } from '../../utils/realtimeErrorHandler';
import ResourceStatusLegend from '../../components/ResourceStatusLegend';

const ProjectPhases = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [phases, setPhases] = useState([]);
  const [deliverables, setDeliverables] = useState([]);
  const [availableResources, setAvailableResources] = useState([]);
  const [isPhaseModalOpen, setIsPhaseModalOpen] = useState(false);
  const [isDeliverableModalOpen, setIsDeliverableModalOpen] = useState(false);
  const [editingPhase, setEditingPhase] = useState(null);
  const [editingDeliverable, setEditingDeliverable] = useState(null);
  const [selectedPhaseId, setSelectedPhaseId] = useState(null);
  const [conflicts, setConflicts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [realtimeError, setRealtimeError] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const [activePhaseIndex, setActivePhaseIndex] = useState(0);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const swipeContainerRef = useRef(null);

  // Load projects and resources on mount
  useEffect(() => {
    loadProjects();
    loadResources();
  }, []);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const { data, error } = await projectService?.getAll();
      if (error) throw error;

      // Transform to match component expectations
      const transformedProjects = (data || [])?.map((project) => ({
        id: project?.id,
        number: project?.projectNumber,
        name: project?.projectName,
        customer: project?.customerName,
        status: project?.status?.toLowerCase()?.replace(' ', '-'),
        totalAmount: parseFloat(project?.totalBudget) || 0,
        startDate: project?.startDate,
        endDate: project?.endDate
      }));

      setProjects(transformedProjects);
    } catch (err) {
      console.error('Error loading projects:', err?.message);
    } finally {
      setLoading(false);
    }
  };

  const loadResources = async () => {
    try {
      const { data, error } = await resourceService?.getAll();
      if (error) throw error;
      setAvailableResources(data || []);
    } catch (err) {
      console.error('Error loading resources:', err?.message);
    }
  };

  // Real-time subscription for project_phases and deliverables
  useEffect(() => {
    if (!selectedProject) return;

    // Load phases and deliverables when project is selected
    loadPhases();
    loadDeliverables();

    // Error handler for subscription
    const handleError = (error) => {
      console.error('Real-time subscription error:', error);
      setRealtimeError(error);
      setIsRetrying(error?.willRetry || false);

      if (!error?.willRetry && error?.type !== RealtimeErrorType?.CONNECTION_FAILED) {
        setTimeout(() => setRealtimeError(null), 5000);
      }
    };

    // Real-time subscription for phases
    const { cleanup: cleanupPhases } = createRealtimeSubscription({
      supabase,
      channelName: 'project_phases_changes',
      tableName: 'project_phases',
      filter: `project_id=eq.${selectedProject?.id}`,
      onDataChange: async () => {
        await loadPhases();
        setRealtimeError(null);
        setIsRetrying(false);
      },
      onError: handleError
    });

    // Real-time subscription for deliverables
    const { cleanup: cleanupDeliverables } = createRealtimeSubscription({
      supabase,
      channelName: 'deliverables_changes',
      tableName: 'deliverables',
      filter: `project_id=eq.${selectedProject?.id}`,
      onDataChange: async () => {
        await loadDeliverables();
        setRealtimeError(null);
        setIsRetrying(false);
      },
      onError: handleError
    });

    // Cleanup subscriptions
    return () => {
      cleanupPhases();
      cleanupDeliverables();
    };
  }, [selectedProject]);

  const loadPhases = async () => {
    if (!selectedProject) return;

    try {
      const { data, error } = await projectService?.getPhases(selectedProject?.id);
      if (error) throw error;

      const transformedPhases = (data || [])?.map((phase) => ({
        id: phase?.id,
        projectId: phase?.projectId,
        name: phase?.name,
        description: phase?.description,
        startDate: phase?.startDate,
        endDate: phase?.endDate,
        status: phase?.status?.toLowerCase()?.replace(' ', '-'),
        totalDeliverables: phase?.deliverableCount || 0,
        completedDeliverables: 0,
        progress: phase?.progress || 0
      }));
      setPhases(transformedPhases);
    } catch (err) {
      console.error('Error loading phases:', err?.message);
    }
  };

  const loadDeliverables = async () => {
    if (!selectedProject) return;

    try {
      const { data, error } = await projectService?.getDeliverables(selectedProject?.id);
      if (error) throw error;

      // Transform deliverables to match component expectations
      const transformedDeliverables = (data || [])?.map((deliverable) => ({
        id: deliverable?.id,
        phaseId: deliverable?.phaseId,
        name: deliverable?.name,
        description: deliverable?.description,
        startDate: deliverable?.startDate,
        endDate: deliverable?.endDate,
        estimatedHours: deliverable?.estimatedHours,
        actualHours: deliverable?.actualHours || 0,
        priority: deliverable?.priority,
        status: deliverable?.status,
        dependsOn: deliverable?.dependsOn || [],
        assignedResources: deliverable?.assignedResources || []
      }));

      setDeliverables(transformedDeliverables);
      detectConflicts(transformedDeliverables);
    } catch (err) {
      console.error('Error loading deliverables:', err?.message);
    }
  };

  const detectConflicts = async (deliverablesData) => {
    if (!selectedProject) return;

    const detectedConflicts = [];

    // Check each resource for conflicts
    for (const deliverable of deliverablesData) {
      for (const resource of deliverable?.assignedResources || []) {
        try {
          const { data: conflicts, error } = await projectService?.checkResourceConflicts(
            selectedProject?.id,
            resource?.id,
            deliverable?.startDate,
            deliverable?.endDate,
            deliverable?.id
          );

          if (error) throw error;

          if (conflicts && conflicts?.length > 0) {
            const existingConflict = detectedConflicts?.find((c) => c?.resource?.id === resource?.id);

            if (existingConflict) {
              // Add to existing conflict
              conflicts?.forEach(conflict => {
                if (!existingConflict?.overlappingAssignments?.some((a) => a?.id === conflict?.id)) {
                  existingConflict?.overlappingAssignments?.push({
                    deliverable: conflict?.name,
                    startDate: conflict?.startDate,
                    endDate: conflict?.endDate
                  });
                }
              });
            } else {
              // Create new conflict entry
              detectedConflicts?.push({
                resource,
                overlappingAssignments: [
                  {
                    deliverable: deliverable?.name,
                    startDate: deliverable?.startDate,
                    endDate: deliverable?.endDate
                  },
                  ...conflicts?.map(conflict => ({
                    deliverable: conflict?.name,
                    startDate: conflict?.startDate,
                    endDate: conflict?.endDate
                  }))
                ]
              });
            }
          }
        } catch (err) {
          console.error('Error checking resource conflicts:', err?.message);
        }
      }
    }

    setConflicts(detectedConflicts);
  };

  const handleSavePhase = async (phaseData) => {
    try {
      if (editingPhase) {
        // Update existing phase
        const { id, deliverableCount, ...updateFields } = phaseData;
        const { data, error } = await projectService?.updatePhase(id, updateFields);
        if (error) throw error;
        await loadPhases();
      } else {
        // Create new phase — strip id and deliverableCount (not DB columns)
        const { id, deliverableCount, ...createFields } = phaseData;
        const { data, error } = await projectService?.createPhase(createFields);
        if (error) throw error;
        await loadPhases();
      }
      setEditingPhase(null);
    } catch (err) {
      console.error('Error saving phase:', err?.message);
      alert('Failed to save phase: ' + err?.message);
    }
  };

  const handleDeletePhase = async (phaseId) => {
    if (window.confirm('Are you sure you want to delete this phase? All associated deliverables will also be deleted.')) {
      try {
        const { error } = await projectService?.deletePhase(phaseId);
        if (error) throw error;
        await loadPhases();
        await loadDeliverables();
      } catch (err) {
        console.error('Error deleting phase:', err?.message);
        alert('Failed to delete phase: ' + err?.message);
      }
    }
  };

  const handleInlineSavePhase = async (phaseId, updates) => {
    try {
      const { error } = await projectService?.updatePhase(phaseId, updates);
      if (error) throw error;
      await loadPhases();
    } catch (err) {
      console.error('Error updating phase:', err?.message);
      alert('Failed to update phase: ' + err?.message);
    }
  };

  const handleSaveDeliverable = async (deliverableData) => {
    try {
      if (editingDeliverable) {
        // Update existing deliverable
        const { data, error } = await projectService?.updateDeliverable(
          deliverableData?.id,
          {
            name: deliverableData?.name,
            description: deliverableData?.description,
            startDate: deliverableData?.startDate,
            endDate: deliverableData?.endDate,
            estimatedHours: deliverableData?.estimatedHours,
            priority: deliverableData?.priority,
            status: deliverableData?.status,
            phaseId: deliverableData?.phaseId,
            dependsOn: deliverableData?.dependsOn || [],
            assignedResources: deliverableData?.assignedResources || []
          }
        );
        if (error) throw error;
      } else {
        // Create new deliverable
        const { data, error } = await projectService?.createDeliverable({
          projectId: selectedProject?.id,
          phaseId: deliverableData?.phaseId,
          name: deliverableData?.name,
          description: deliverableData?.description,
          startDate: deliverableData?.startDate,
          endDate: deliverableData?.endDate,
          estimatedHours: deliverableData?.estimatedHours,
          priority: deliverableData?.priority,
          status: deliverableData?.status,
          dependsOn: deliverableData?.dependsOn || [],
          assignedResources: deliverableData?.assignedResources || []
        });
        if (error) throw error;
      }

      await loadDeliverables();
      setEditingDeliverable(null);
      setSelectedPhaseId(null);
    } catch (err) {
      console.error('Error saving deliverable:', err?.message);
      alert('Failed to save deliverable: ' + err?.message);
    }
  };

  const handleDeleteDeliverable = async (deliverableId) => {
    if (window.confirm('Are you sure you want to delete this deliverable?')) {
      try {
        const { error } = await projectService?.deleteDeliverable(deliverableId);
        if (error) throw error;
        await loadDeliverables();
      } catch (err) {
        console.error('Error deleting deliverable:', err?.message);
        alert('Failed to delete deliverable: ' + err?.message);
      }
    }
  };

  const handleUpdateDeliverableStatus = async (deliverableId, newStatus) => {
    try {
      const { data, error } = await projectService?.updateDeliverable(deliverableId, { status: newStatus });
      if (error) throw error;
      await loadDeliverables();
    } catch (err) {
      console.error('Error updating deliverable status:', err?.message);
      alert('Failed to update status: ' + err?.message);
    }
  };

  const getPhaseDeliverables = (phaseId) => {
    return deliverables?.filter((d) => d?.phaseId === phaseId);
  };

  // Compute conflict count per phase based on detected conflicts
  const getPhaseConflictCount = (phaseId) => {
    const phaseDeliverableIds = new Set(
      deliverables?.filter(d => d?.phaseId === phaseId)?.map(d => d?.id)
    );
    // Count unique resources with conflicts that are assigned to deliverables in this phase
    const conflictingResources = new Set();
    conflicts?.forEach(conflict => {
      const resourceId = conflict?.resource?.id;
      // Check if any overlapping assignment belongs to a deliverable in this phase
      const hasPhaseDeliverable = conflict?.overlappingAssignments?.some(a =>
        deliverables?.some(d => d?.phaseId === phaseId && d?.name === a?.deliverable)
      );
      if (hasPhaseDeliverable) conflictingResources?.add(resourceId);
    });
    return conflictingResources?.size;
  };

  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e?.touches?.[0]?.clientX;
    touchStartY.current = e?.touches?.[0]?.clientY;
  }, []);

  const handleTouchEnd = useCallback((e) => {
    if (touchStartX?.current === null) return;
    const deltaX = e?.changedTouches?.[0]?.clientX - touchStartX?.current;
    const deltaY = e?.changedTouches?.[0]?.clientY - (touchStartY?.current || 0);
    // Only swipe if horizontal movement is dominant and > 50px
    if (Math.abs(deltaX) > 50 && Math.abs(deltaX) > Math.abs(deltaY) * 1.5) {
      if (deltaX < 0 && activePhaseIndex < phases?.length - 1) {
        setActivePhaseIndex(prev => prev + 1);
      } else if (deltaX > 0 && activePhaseIndex > 0) {
        setActivePhaseIndex(prev => prev - 1);
      }
    }
    touchStartX.current = null;
    touchStartY.current = null;
  }, [activePhaseIndex, phases?.length]);

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
              Project Phases & Deliverables
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">
              Manage project phases, track deliverables, and coordinate resource assignments
            </p>
          </div>
        </div>

        <ProjectSelector
          projects={projects}
          selectedProject={selectedProject}
          onSelectProject={setSelectedProject} />

        {/* Summary Stats - shown when a project is selected */}
        {selectedProject && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            <div className="bg-card rounded-lg border border-border p-4 md:p-6">
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-accent/10 flex items-center justify-center">
                  <Icon name="GitBranch" size={20} className="text-accent" />
                </div>
                <span className="text-xs md:text-sm font-medium text-muted-foreground">Total</span>
              </div>
              <p className="text-2xl md:text-3xl font-bold text-foreground mb-1">{phases?.length || 0}</p>
              <p className="text-xs md:text-sm text-muted-foreground">Total Phases</p>
            </div>

            <div className="bg-card rounded-lg border border-border p-4 md:p-6">
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <Icon name="Package" size={20} className="text-blue-500" />
                </div>
                <span className="text-xs md:text-sm font-medium text-muted-foreground">Total</span>
              </div>
              <p className="text-2xl md:text-3xl font-bold text-foreground mb-1">{deliverables?.length || 0}</p>
              <p className="text-xs md:text-sm text-muted-foreground">Total Deliverables</p>
            </div>

            <div className="bg-card rounded-lg border border-border p-4 md:p-6">
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <Icon name="CheckCircle2" size={20} className="text-green-500" />
                </div>
                <span className="text-xs md:text-sm font-medium text-muted-foreground">Done</span>
              </div>
              <p className="text-2xl md:text-3xl font-bold text-foreground mb-1">
                {deliverables?.filter(d => d?.status === 'Completed')?.length || 0}
              </p>
              <p className="text-xs md:text-sm text-muted-foreground">Completed Deliverables</p>
            </div>

            <div className="bg-card rounded-lg border border-border p-4 md:p-6">
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                  <Icon name="AlertTriangle" size={20} className="text-yellow-500" />
                </div>
                <span className="text-xs md:text-sm font-medium text-muted-foreground">Issues</span>
              </div>
              <p className="text-2xl md:text-3xl font-bold text-foreground mb-1">{conflicts?.length || 0}</p>
              <p className="text-xs md:text-sm text-muted-foreground">Resource Conflicts</p>
            </div>
          </div>
        )}

        {selectedProject &&
        <>
            <ResourceConflictAlert conflicts={conflicts} />

            <div className="bg-card rounded-xl border border-border p-4 md:p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg md:text-xl font-semibold text-foreground">
                  Project Timeline
                </h2>
                <Button
                variant="default"
                iconName="Plus"
                iconPosition="left"
                onClick={() => {
                  setEditingPhase(null);
                  setIsPhaseModalOpen(true);
                }}>
                  Add Phase
                </Button>
              </div>

              {phases?.length === 0 ?
            <div className="text-center py-12">
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="GitBranch" size={32} color="var(--color-muted-foreground)" />
                  </div>
                  <h3 className="text-lg font-medium text-foreground mb-2">No Phases Yet</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Start by adding the first phase to this project
                  </p>
                  <Button
                variant="outline"
                iconName="Plus"
                iconPosition="left"
                onClick={() => {
                  setEditingPhase(null);
                  setIsPhaseModalOpen(true);
                }}>
                    Add First Phase
                  </Button>
                </div> :

            <>
                  {/* ── MOBILE: swipe-to-navigate one phase at a time ── */}
                  <div className="lg:hidden">
                    {/* Phase counter + dot indicators */}
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm font-medium text-muted-foreground">
                        Phase {activePhaseIndex + 1} of {phases?.length}
                      </span>
                      <div className="flex items-center gap-1.5">
                        {phases?.map((_, idx) => (
                          <button
                            key={idx}
                            onClick={() => setActivePhaseIndex(idx)}
                            className={`rounded-full transition-all duration-200 ${
                              idx === activePhaseIndex
                                ? 'w-5 h-2 bg-primary' :'w-2 h-2 bg-muted-foreground/30 hover:bg-muted-foreground/60'
                            }`}
                            aria-label={`Go to phase ${idx + 1}`}
                          />
                        ))}
                      </div>
                    </div>

                    {/* Swipeable phase card */}
                    <div
                      ref={swipeContainerRef}
                      onTouchStart={handleTouchStart}
                      onTouchEnd={handleTouchEnd}
                      className="touch-pan-y select-none"
                    >
                      {phases?.[activePhaseIndex] && (
                        <div>
                          <PhaseTimelineCard
                            phase={phases?.[activePhaseIndex]}
                            isFirst={activePhaseIndex === 0}
                            isLast={activePhaseIndex === phases?.length - 1}
                            conflictCount={getPhaseConflictCount(phases?.[activePhaseIndex]?.id)}
                            onEdit={(p) => {
                              setEditingPhase(p);
                              setIsPhaseModalOpen(true);
                            }}
                            onDelete={handleDeletePhase}
                            onInlineSave={handleInlineSavePhase}
                          />

                          <div className="mt-4 space-y-4">
                            <div className="flex items-center justify-between">
                              <h3 className="text-base font-medium text-foreground">
                                Deliverables
                              </h3>
                              <Button
                                variant="outline"
                                size="sm"
                                iconName="Plus"
                                iconPosition="left"
                                onClick={() => {
                                  setSelectedPhaseId(phases?.[activePhaseIndex]?.id);
                                  setEditingDeliverable(null);
                                  setIsDeliverableModalOpen(true);
                                }}>
                                Add Deliverable
                              </Button>
                            </div>

                            {getPhaseDeliverables(phases?.[activePhaseIndex]?.id)?.length === 0 ? (
                              <div className="bg-muted rounded-lg p-6 text-center">
                                <Icon name="Package" size={24} color="var(--color-muted-foreground)" className="mx-auto mb-2" />
                                <p className="text-sm text-muted-foreground">
                                  No deliverables in this phase yet
                                </p>
                              </div>
                            ) : (
                              <div className="space-y-3">
                                {getPhaseDeliverables(phases?.[activePhaseIndex]?.id)?.map((deliverable) => (
                                  <DeliverableRow
                                    key={deliverable?.id}
                                    deliverable={deliverable}
                                    isMobile={true}
                                    onEdit={(d) => {
                                      setSelectedPhaseId(phases?.[activePhaseIndex]?.id);
                                      setEditingDeliverable(d);
                                      setIsDeliverableModalOpen(true);
                                    }}
                                    onDelete={handleDeleteDeliverable}
                                    onUpdateStatus={handleUpdateDeliverableStatus}
                                  />
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Prev / Next navigation buttons */}
                    <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
                      <button
                        onClick={() => setActivePhaseIndex(prev => Math.max(0, prev - 1))}
                        disabled={activePhaseIndex === 0}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-30 disabled:cursor-not-allowed bg-muted hover:bg-muted/80 text-foreground"
                      >
                        <Icon name="ChevronLeft" size={16} />
                        Previous
                      </button>
                      <button
                        onClick={() => setActivePhaseIndex(prev => Math.min(phases?.length - 1, prev + 1))}
                        disabled={activePhaseIndex === phases?.length - 1}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-30 disabled:cursor-not-allowed bg-muted hover:bg-muted/80 text-foreground"
                      >
                        Next
                        <Icon name="ChevronRight" size={16} />
                      </button>
                    </div>
                  </div>

                  {/* ── DESKTOP: original stacked layout ── */}
                  <div className="hidden lg:block space-y-6">
                    {phases?.map((phase, index) =>
                      <div key={phase?.id}>
                        <PhaseTimelineCard
                          phase={phase}
                          isFirst={index === 0}
                          isLast={index === phases?.length - 1}
                          conflictCount={getPhaseConflictCount(phase?.id)}
                          onEdit={(p) => {
                            setEditingPhase(p);
                            setIsPhaseModalOpen(true);
                          }}
                          onDelete={handleDeletePhase}
                          onInlineSave={handleInlineSavePhase}
                        />

                        <div className="mt-4 ml-16 space-y-4">
                          <div className="flex items-center justify-between">
                            <h3 className="text-base md:text-lg font-medium text-foreground">
                              Deliverables
                            </h3>
                            <Button
                              variant="outline"
                              size="sm"
                              iconName="Plus"
                              iconPosition="left"
                              onClick={() => {
                                setSelectedPhaseId(phase?.id);
                                setEditingDeliverable(null);
                                setIsDeliverableModalOpen(true);
                              }}>
                              Add Deliverable
                            </Button>
                          </div>

                          {getPhaseDeliverables(phase?.id)?.length === 0 ?
                            <div className="bg-muted rounded-lg p-6 text-center">
                              <Icon name="Package" size={24} color="var(--color-muted-foreground)" className="mx-auto mb-2" />
                              <p className="text-sm text-muted-foreground">
                                No deliverables in this phase yet
                              </p>
                            </div> :
                            <div className="space-y-3">
                              {getPhaseDeliverables(phase?.id)?.map((deliverable) =>
                                <DeliverableRow
                                  key={deliverable?.id}
                                  deliverable={deliverable}
                                  onEdit={(d) => {
                                    setSelectedPhaseId(phase?.id);
                                    setEditingDeliverable(d);
                                    setIsDeliverableModalOpen(true);
                                  }}
                                  onDelete={handleDeleteDeliverable}
                                  onUpdateStatus={handleUpdateDeliverableStatus}
                                  isMobile={false}
                                />
                              )}
                            </div>
                          }
                        </div>
                      </div>
                    )}
                  </div>
                </>
            }
            </div>
          </>
        }

        {!selectedProject &&
        <div className="bg-card rounded-xl border border-border p-12 text-center">
            <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="FolderOpen" size={40} color="var(--color-muted-foreground)" />
            </div>
            <h3 className="text-xl font-semibold text-foreground mb-2">
              Select a Project
            </h3>
            <p className="text-base text-muted-foreground">
              Choose a project from the dropdown above to view and manage its phases and deliverables
            </p>
          </div>
        }

        {/* Status Legend */}
        <ResourceStatusLegend />
      </div>
      <AddPhaseModal
        isOpen={isPhaseModalOpen}
        onClose={() => {
          setIsPhaseModalOpen(false);
          setEditingPhase(null);
        }}
        onSave={handleSavePhase}
        editingPhase={editingPhase}
        projectId={selectedProject?.id} />
      <AddDeliverableModal
        isOpen={isDeliverableModalOpen}
        onClose={() => {
          setIsDeliverableModalOpen(false);
          setEditingDeliverable(null);
          setSelectedPhaseId(null);
        }}
        onSave={handleSaveDeliverable}
        editingDeliverable={editingDeliverable}
        phaseId={selectedPhaseId}
        phase={phases?.find(p => p?.id === selectedPhaseId) || null}
        availableResources={availableResources} />
    </MainLayout>
  );

};

export default ProjectPhases;